// ============================================================
// ピースビルダー トップ一覧 UI 専用型
//
// 共有 types.ts（③ビルダーが一次所有・改変中）との衝突回避のため
// 一覧系の型はこのファイルに分離。状態 enum は types.ts の PieceStatus を再利用。
// API 契約: docs/piece-builder/api-contract.md（api/src/routes/piece/types.ts と対応）
// 注: 現状は UI のみ（ダミーデータ）。実 API 接続は後続フェーズ。
// ============================================================

import { PieceStatus, derivePieceStatus, ApiPieceListItem } from "features/builder/types";

// 一覧の並び替え軸（api-contract.md。skill 規約により enum 化）
// DELETED / VIEWED は UI 用クライアントソート軸（実 API 接続時は対応要確認）。
export enum PieceSort {
  UPDATED = "updated",
  DELETED = "deleted",
  LIKED = "liked",
  PLAYED = "played",
  VIEWED = "viewed",
  REACTION = "reaction",
}

export enum PieceSortOrder {
  DESC = "desc",
  ASC = "asc",
}

// 一覧タブの文脈（カードの表示・操作を切り替える）
export enum PieceListContext {
  MINE = "mine",
  PUBLIC = "public",
  TRASH = "trash",
}

// 一覧の表示レイアウト（カラム数 / 圧縮リスト）
export enum PieceLayoutMode {
  GRID_1 = "grid_1",
  GRID_2 = "grid_2",
  LIST = "list",
}

// じぶんのピース タブの絞り込み（公開軸 + つくりかけ/編集中/おきにいりトグル。組合せ可）
export enum PublishedFilter {
  ALL = "all",
  PUBLISHED = "published",
  UNPUBLISHED = "unpublished",
}

export type MineFilter = {
  published: PublishedFilter;
  // つくりかけ（未完成）だけ表示
  incomplete: boolean;
  editing: boolean;
  favorite: boolean;
};

export const DEFAULT_MINE_FILTER: MineFilter = {
  published: PublishedFilter.ALL,
  incomplete: false,
  editing: false,
  favorite: false,
};

// みんなのピース タブの絞り込み（MINE とは独立。PUBLIC 文脈に合わせた専用項目）
// しゅるい軸（公式/非公式）＋ お気に入り/コピー可/リミックス/にんき/コメント可（組合せ可）
export enum PublicOfficialFilter {
  ALL = "all",
  OFFICIAL = "official",
  UNOFFICIAL = "unofficial",
}

// にんき（liked）の基準しきい値
export const POPULAR_THRESHOLD = 100;

export type PublicFilterState = {
  official: PublicOfficialFilter;
  favorite: boolean; // is_liked
  copyable: boolean; // enable_copied
  remix: boolean; // copied_piece_id !== null
  popular: boolean; // liked >= POPULAR_THRESHOLD
  commentable: boolean; // enable_comment
};

export const DEFAULT_PUBLIC_FILTER: PublicFilterState = {
  official: PublicOfficialFilter.ALL,
  favorite: false,
  copyable: false,
  remix: false,
  popular: false,
  commentable: false,
};

// ============================================================
// 左パネル UI 状態（redux 永続化）
// タブ選択・各タブのフィルタ/並び替え/ひょうじ/スクロール位置を保持し、
// タブ切替・くわしく往復・ビルダー編集往復で復元する。
// ============================================================

export type MineListUi = {
  filter: MineFilter;
  sortAxis: PieceSort;
  sortOrder: PieceSortOrder;
  layoutMode: PieceLayoutMode;
  scrollTop: number;
};

export type PublicListUi = {
  filter: PublicFilterState;
  sortAxis: PieceSort;
  sortOrder: PieceSortOrder;
  layoutMode: PieceLayoutMode;
  scrollTop: number;
};

export type TrashListUi = {
  sortAxis: PieceSort;
  sortOrder: PieceSortOrder;
  layoutMode: PieceLayoutMode;
  scrollTop: number;
};

export type PieceListUiState = {
  activeTab: PieceListContext;
  mine: MineListUi;
  public: PublicListUi;
  trash: TrashListUi;
};

export const DEFAULT_PIECE_LIST_UI: PieceListUiState = {
  activeTab: PieceListContext.MINE,
  mine: {
    filter: DEFAULT_MINE_FILTER,
    sortAxis: PieceSort.UPDATED,
    sortOrder: PieceSortOrder.DESC,
    layoutMode: PieceLayoutMode.GRID_2,
    scrollTop: 0,
  },
  public: {
    filter: DEFAULT_PUBLIC_FILTER,
    sortAxis: PieceSort.LIKED,
    sortOrder: PieceSortOrder.DESC,
    layoutMode: PieceLayoutMode.GRID_2,
    scrollTop: 0,
  },
  trash: {
    sortAxis: PieceSort.DELETED,
    sortOrder: PieceSortOrder.DESC,
    layoutMode: PieceLayoutMode.GRID_2,
    scrollTop: 0,
  },
};

// カードから発火するアクション（タブ側でハンドリング）
export enum PieceCardAction {
  DETAIL = "detail",
  PLAY = "play",
  EDIT = "edit",
  COPY = "copy",
  RESTORE = "restore",
  DELETE_FOREVER = "delete_forever",
  TO_TRASH = "to_trash",
  HINT = "hint",
  LIKE = "like",
  REACTION = "reaction",
  REPORT = "report",
  TOGGLE_PUBLISH = "toggle_publish",
}

// 6 種リアクションのカウント（将来用、MVP では更新しない）
export type PieceReaction = {
  difficult: number;
  easy: number;
  cool: number;
  fun: number;
  amazing: number;
  bad: number;
};

// piece テーブル行（メタデータ + 一覧ソート用の非正規化カウンタ）
export type Piece = {
  piece_id: string;
  inquiry_id: string;
  name: string;
  description: string;
  capture: string | null;
  cover: string | null;
  author_uid: string;
  copied_piece_id: string | null;
  original_piece_id: string | null;
  original_author_uid: string | null;
  original_piece_created_at: string | null;
  status: PieceStatus;
  status_before_delete: PieceStatus | null;
  // ユーザーの明示「完成」操作で確定。公開とは独立
  completed: boolean;
  completed_at: string | null;
  published: boolean;
  enable_copied: boolean;
  enable_comment: boolean;
  // 公式テンプレート（「こうしき」リボン表示判定）
  is_official: boolean;
  // 完成回数（POST /complete のたび +1。公開では増えない）
  version: number;
  liked: number;
  viewed: number;
  copied: number;
  played: number;
  cleared: number;
  reaction: PieceReaction | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  deleted_at: string | null;
  // 管理画面で処分されたピース（作者には見えるが、公開できない）
  banned: boolean;
};

// 一覧 API が返す JOIN 済みアイテム
export type PieceListItem = Piece & {
  author_name: string | null;
  author_icon: string | null;
  original_author_name: string | null;
  original_author_icon: string | null;
  is_liked: boolean;
  // 閲覧者がこのピースに付けたリアクション種（詳細でのみ充足）
  my_reactions?: string[];
  /** 管理画面で処分された（作者には見えるが公開できない） */
  banned: boolean;
};

// ダミーデータ専用拡張（capture/cover が無いとき絵文字プレースホルダを表示）。
// API 型（PieceListItem）は汚さない。実 API 接続時は emoji を使わない。
export type MockPiece = PieceListItem & { emoji?: string };

// サーバー応答（ApiPieceListItem：status を返さない）→ カード表示用 PieceListItem へ変換。
// status / status_before_delete は3軸フラグから derivePieceStatus で導出する
// （API は status を定義しない、という設計に従う）。
export const toCardPiece = (api: ApiPieceListItem): MockPiece => ({
  piece_id: api.piece_id,
  inquiry_id: api.inquiry_id,
  name: api.name,
  description: api.description,
  capture: api.capture,
  cover: api.cover,
  author_uid: api.author_uid,
  copied_piece_id: api.copied_piece_id,
  original_piece_id: api.original_piece_id,
  original_author_uid: api.original_author_uid,
  original_piece_created_at: api.original_piece_created_at,
  status: derivePieceStatus(api),
  status_before_delete: api.deleted_at ? derivePieceStatus({ ...api, deleted_at: null }) : null,
  completed: api.completed,
  completed_at: api.completed_at,
  published: api.published,
  enable_copied: api.enable_copied,
  enable_comment: api.enable_comment,
  is_official: api.is_official,
  version: api.version,
  liked: api.liked,
  viewed: api.viewed,
  copied: api.copied,
  played: api.played,
  cleared: api.cleared,
  reaction: api.reaction,
  created_at: api.created_at,
  updated_at: api.updated_at,
  published_at: api.published_at,
  deleted_at: api.deleted_at,
  banned: api.banned,
  author_name: api.author_name,
  author_icon: api.author_icon,
  original_author_name: api.original_author_name,
  original_author_icon: api.original_author_icon,
  is_liked: api.is_liked,
  my_reactions: api.my_reactions ?? [],
});

// 6 種リアクションの合計（ソート用）
export const reactionTotal = (p: Piece): number =>
  p.reaction
    ? p.reaction.difficult +
      p.reaction.easy +
      p.reaction.cool +
      p.reaction.fun +
      p.reaction.amazing +
      p.reaction.bad
    : 0;

// 日付軸（順序ラベルを「あたらしい/ふるい」にするかの判定にも使う）
export const isDateAxis = (axis: PieceSort): boolean =>
  axis === PieceSort.UPDATED || axis === PieceSort.DELETED;

// 軸 × 昇順/降順の比較関数（DESC = 新しい/多い順、ASC = 古い/少ない順）
export const comparePieces =
  (axis: PieceSort, order: PieceSortOrder) =>
  (a: Piece, b: Piece): number => {
    const dir = order === PieceSortOrder.DESC ? -1 : 1;
    switch (axis) {
      case PieceSort.UPDATED:
        return dir * a.updated_at.localeCompare(b.updated_at);
      case PieceSort.DELETED:
        return dir * (a.deleted_at ?? "").localeCompare(b.deleted_at ?? "");
      case PieceSort.LIKED:
        return dir * (a.liked - b.liked);
      case PieceSort.PLAYED:
        return dir * (a.played - b.played);
      case PieceSort.VIEWED:
        return dir * (a.viewed - b.viewed);
      case PieceSort.REACTION:
        return dir * (reactionTotal(a) - reactionTotal(b));
    }
  };
